/**
 * Unverified agent journey — protocol `blocked` (ADR-0159 §3).
 *
 * The existing "blocked at the door" identity-gate rejection stays as-is
 * (ADR-0134 pattern). Discovery + read-menu are public and run first;
 * identity is the gate that stops the unverified agent.
 *
 * The blocked-path banner CARD (outside the timeline) is still rendered
 * by `Simulation.tsx` when the picked agent's protocol is `'blocked'`
 * and the run has advanced past the identity phase — this preserves the
 * existing test assertion on "blocked at the door".
 */

import { buildDiscoveryPhase, buildReadMenuPhase } from './discovery.js';
import type { JourneyContext, Phase } from './types.js';

export function buildUnverifiedJourney(ctx: JourneyContext): Phase[] {
  const { agent, live, installed, cardUrl, catalogUrl, liveProducts, cart } = ctx;
  return [
    buildDiscoveryPhase(agent, installed, cardUrl, live.card),
    buildReadMenuPhase(agent, installed, catalogUrl, live.catalog, liveProducts),
    {
      id: 'identity',
      name: 'Identity check',
      tier: 'illustration',
      narration:
        `This agent shows no signed identity. Sill stops it at the door — before any transaction, no data exposed.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 403,
        ms: 0,
        body: '{ "error": "identity_check_failed" }',
        live: false,
      },
      ok: false,
    },
    // Skipped illustrative phases — the unverified path never gets past
    // identity. The visible skipped rows mirror the original view's
    // "Skipped — agent didn't get past identity check." narration.
    {
      id: 'intent',
      name: 'Intent mandate',
      tier: 'illustration',
      narration: cart.unit_price_display.length > 0
        ? `${agent.name}'s user confirmed "buy 2× ${cart.title}${cart.is_sample ? ' [sample]' : ''} around ${cart.line_total_display}." It issues a signed intent mandate.`
        : `${agent.name}'s user confirmed "buy 2× ${cart.title}${cart.is_sample ? ' [sample]' : ''}." It issues a signed intent mandate.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    },
    {
      id: 'cart',
      name: 'Cart',
      tier: 'illustration',
      narration: `(skipped)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    },
    {
      id: 'pay',
      name: 'Payment',
      tier: 'illustration',
      narration: `(skipped)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    },
    {
      id: 'fulfill',
      name: 'Fulfillment',
      tier: 'illustration',
      narration: `(skipped)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    },
  ];
}
